import { useCallback, useEffect, useMemo, useState } from "react";
import { FaBoxes } from "react-icons/fa";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import {
  repositorioEstoqueItem,
  repositorioLocalEstoque,
  repositorioMovimentacaoEstoque,
} from "../../sync/repositories/almoxarifado";

const AJUDA = {
  titulo: "Consulta de Movimentações de Estoque",
  descricao:
    "Esta tela é só de consulta/histórico — não existe cadastro manual de movimentação aqui. Toda entrada, saída ou ajuste que passa pelo estoque aparece automaticamente nesta lista: acertos feitos na tela de Acerto de Estoque, e entradas geradas a partir de notas fiscais e lançamentos financeiros de compra vinculados a itens de estoque. Use os filtros abaixo para localizar movimentações específicas.",
  secoes: [
    {
      titulo: "Filtros Disponíveis",
      campos: [
        {
          nome: "Tipo de Movimentação",
          obrigatorio: false,
          explicacao:
            "Filtra por Entrada, Saída ou Ajuste, conforme a natureza do lançamento.",
        },
        {
          nome: "Item",
          obrigatorio: false,
          explicacao: "Mostra apenas as movimentações de um item específico do estoque.",
        },
        {
          nome: "Local",
          obrigatorio: false,
          explicacao:
            "Mostra apenas as movimentações ocorridas em um Local de Estoque específico.",
        },
        {
          nome: "Data Início / Data Fim",
          obrigatorio: false,
          explicacao:
            "Restringe o período analisado — útil para fechar um mês ou conferir um intervalo específico.",
        },
        {
          nome: "Referência / Documento",
          obrigatorio: false,
          explicacao:
            "Busca por texto no campo de referência (ex.: número da nota fiscal ou 'ACERTO DE ESTOQUE').",
        },
      ],
    },
  ],
  dicas: [
    "Os cartões no topo (Entradas, Saídas, Saldo Atual e Valor Total) somam apenas as movimentações exibidas com os filtros aplicados — mudam conforme você filtra.",
    "Para lançar uma movimentação manual, use a tela de Acerto de Estoque — as movimentações vindas de notas fiscais e lançamentos financeiros são geradas automaticamente pelo sistema.",
  ],
};

export default function GestaoMovimentacoesEstoque() {
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [movimentacoesFiltradas, setMovimentacoesFiltradas] = useState([]);
  
  // Dados para os selects
  const [itens, setItens] = useState([]);
  const [locais, setLocais] = useState([]);
  
  // Filtros
  const [filtros, setFiltros] = useState({
    tipo: "",
    item: "",
    local: "",
    referencia: "",
    data_inicio: "",
    data_fim: ""
  });
  
  // Paginação
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina] = useState(10);

  // Tipos de movimentação
  const tiposMovimentacao = [
    { value: "entrada", label: "Entrada", cor: "bg-green-100 text-green-800" },
    { value: "saida", label: "Saída", cor: "bg-red-100 text-red-800" },
    { value: "ajuste", label: "Ajuste", cor: "bg-yellow-100 text-yellow-800" },
  ];

  // Carregar dados
  const carregarDados = useCallback(async () => {
    try {
      setCarregandoLista(true);
      const [itensRes, locaisRes, movRes] = await Promise.all([
        repositorioEstoqueItem.listar(),
        repositorioLocalEstoque.listar(),
        repositorioMovimentacaoEstoque.listar(),
      ]);

      setItens(Array.isArray(itensRes.data) ? itensRes.data : (itensRes.data.results || []));
      setLocais(Array.isArray(locaisRes.data) ? locaisRes.data : (locaisRes.data.results || []));
      const movimentos = Array.isArray(movRes.data) ? movRes.data : (movRes.data.results || []);
      setMovimentacoes(movimentos);
      setMovimentacoesFiltradas(movimentos);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      alert("Erro ao carregar dados");
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = [...movimentacoes];
    
    if (filtros.tipo) {
      filtrados = filtrados.filter(m => m.tipo_movimentacao === filtros.tipo);
    }
    
    if (filtros.item) {
      filtrados = filtrados.filter(m => String(m.item) === filtros.item);
    }
    
    if (filtros.local) {
      filtrados = filtrados.filter(m => String(m.local) === filtros.local);
    }
    
    if (filtros.referencia) {
      const refLower = filtros.referencia.toLowerCase();
      filtrados = filtrados.filter(m => 
        m.referencia?.toLowerCase().includes(refLower)
      );
    }
    
    if (filtros.data_inicio) {
      filtrados = filtrados.filter(m => m.data_movimentacao >= filtros.data_inicio);
    }
    
    if (filtros.data_fim) {
      filtrados = filtrados.filter(m => m.data_movimentacao <= filtros.data_fim);
    }
    
    setMovimentacoesFiltradas(filtrados);
    setPaginaAtual(1);
  };

  const limparFiltros = () => {
    setFiltros({
      tipo: "",
      item: "",
      local: "",
      referencia: "",
      data_inicio: "",
      data_fim: ""
    });
    setMovimentacoesFiltradas(movimentacoes);
    setPaginaAtual(1);
  };

  // Resumo dos dados filtrados
  const resumo = useMemo(() => {
    const entradas = movimentacoesFiltradas
      .filter(m => m.tipo_movimentacao === "entrada")
      .reduce((acc, item) => acc + Number(item.quantidade), 0);

    const saidas = movimentacoesFiltradas
      .filter(m => m.tipo_movimentacao === "saida")
      .reduce((acc, item) => acc + Number(item.quantidade), 0);

    const valorTotal = movimentacoesFiltradas.reduce(
      (acc, item) => acc + (Number(item.quantidade) * (Number(item.valor_unitario_custo) || 0)), 0
    );

    return { entradas, saidas, saldo: entradas - saidas, valorTotal };
  }, [movimentacoesFiltradas]);

  const formatarData = (data) => {
    if (!data) return "—";
    return new Date(data).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  };

  const formatarMoeda = (valor) => {
    if (!valor && valor !== 0) return "R$ 0,00";
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
  };

  const getTipoInfo = (tipo) => {
    const tipoInfo = tiposMovimentacao.find(t => t.value === tipo);
    return tipoInfo || { label: tipo, cor: "bg-gray-100 text-gray-800" };
  };

  const getItemNome = (item) => {
    if (!item) return "—";

    // Se já vier objeto do backend
    if (typeof item === "object") {
      return item.nome || "—";
    }

    // Se vier apenas ID
    const itemEncontrado = itens.find(
      (i) => String(i.id) === String(item)
    );

    return itemEncontrado?.nome || item;
  };

  const getLocalNome = (local) => {
    if (!local) return "—";

    if (typeof local === "object") {
      return local.nome || "—";
    }

    const localEncontrado = locais.find(
      (l) => String(l.id) === String(local)
    );

    return localEncontrado?.nome || local;
  };

  // Paginação
  const indiceUltimoItem = paginaAtual * itensPorPagina;
  const indicePrimeiroItem = indiceUltimoItem - itensPorPagina;
  const movimentacoesPaginadas = movimentacoesFiltradas.slice(indicePrimeiroItem, indiceUltimoItem);
  const totalPaginas = Math.ceil(movimentacoesFiltradas.length / itensPorPagina);

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Movimentações de Estoque <FaBoxes size={38} /></h2>
            <AjudaTela {...AJUDA} />
          </div>
        </div>

        {/* Cards de Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-green-500">
            <p className="text-sm text-gray-500">Total de Entradas</p>
            <h3 className="text-2xl font-bold text-green-600">{resumo.entradas.toLocaleString('pt-BR')}</h3>
          </div>

          <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-red-500">
            <p className="text-sm text-gray-500">Total de Saídas</p>
            <h3 className="text-2xl font-bold text-red-600">{resumo.saidas.toLocaleString('pt-BR')}</h3>
          </div>

          <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-blue-500">
            <p className="text-sm text-gray-500">Saldo Atual</p>
            <h3 className="text-2xl font-bold text-blue-600">{resumo.saldo.toLocaleString('pt-BR')}</h3>
          </div>

          <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-purple-500">
            <p className="text-sm text-gray-500">Valor Total em Estoque</p>
            <h3 className="text-2xl font-bold text-purple-600">{formatarMoeda(resumo.valorTotal)}</h3>
          </div>
        </div>

        {/* Seção de Filtros */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filtros de Busca
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">Tipo de Movimentação</label>
              <select
                value={filtros.tipo}
                onChange={(e) => setFiltros({...filtros, tipo: e.target.value})}
                className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
              >
                <option value="">Todos os tipos</option>
                {tiposMovimentacao.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">Item</label>
              <select
                value={filtros.item}
                onChange={(e) => setFiltros({...filtros, item: e.target.value})}
                className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
              >
                <option value="">Todos os itens</option>
                {itens.map(i => (
                  <option key={i.id} value={i.id}>{i.nome}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">Local</label>
              <select
                value={filtros.local}
                onChange={(e) => setFiltros({...filtros, local: e.target.value})}
                className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
              >
                <option value="">Todos os locais</option>
                {locais.map(l => (
                  <option key={l.id} value={l.id}>{l.nome}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">Data Início</label>
              <input
                type="date"
                value={filtros.data_inicio}
                onChange={(e) => setFiltros({...filtros, data_inicio: e.target.value})}
                className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">Data Fim</label>
              <input
                type="date"
                value={filtros.data_fim}
                onChange={(e) => setFiltros({...filtros, data_fim: e.target.value})}
                className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">Referência / Documento</label>
              <input
                type="text"
                placeholder="Nota fiscal, pedido, etc..."
                value={filtros.referencia}
                onChange={(e) => setFiltros({...filtros, referencia: e.target.value})}
                className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
              />
            </div>
          </div>
          
          <div className="flex gap-3 mt-4">
            <button
              onClick={aplicarFiltros}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
            >
              Aplicar Filtros
            </button>
            <button
              onClick={limparFiltros}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
            >
              Limpar Filtros
            </button>
          </div>
        </div>

        {/* Tabela de Movimentações */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center flex-wrap gap-2">
            <p className="text-gray-600">
              {movimentacoesFiltradas.length} movimentação(ões) encontrada(s)
            </p>
            <div className="text-sm text-gray-500">
              Página {paginaAtual} de {totalPaginas || 1}
            </div>
          </div>
          
          {carregandoLista ? (
            <div className="p-8 text-center text-gray-500">Carregando...</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Data</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Item</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Tipo</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Quantidade</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Local</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Valor Unitário</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Valor Total</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Referência</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Observação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movimentacoesPaginadas.map((mov) => {
                      const tipoInfo = getTipoInfo(mov.tipo_movimentacao);
                      const valorTotal = (mov.quantidade || 0) * (mov.valor_unitario_custo || 0);
                      return (
                        <tr key={mov.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                          <td className="p-3 text-sm">{formatarData(mov.data_movimentacao)}</td>
                          <td className="p-3 text-sm font-medium">
                            {mov.item_detalhe?.nome || getItemNome(mov.item)}
                          </td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${tipoInfo.cor}`}>
                              {tipoInfo.label}
                            </span>
                          </td>
                          <td className="p-3 text-sm text-right font-medium">{mov.quantidade?.toLocaleString('pt-BR')}</td>
                          <td className="p-3 text-sm">
                            {mov.local_detalhe?.nome || getLocalNome(mov.local)}
                          </td>
                          <td className="p-3 text-sm text-right">{formatarMoeda(mov.valor_unitario_custo)}</td>
                          <td className="p-3 text-sm text-right font-medium">{formatarMoeda(valorTotal)}</td>
                          <td className="p-3 text-sm">{mov.referencia || "—"}</td>
                          <td className="p-3 text-sm max-w-xs truncate">{mov.observacao || "—"}</td>
                        </tr>
                      );
                    })}
                    
                    {movimentacoesPaginadas.length === 0 && (
                      <tr>
                        <td colSpan="9" className="p-8 text-center text-gray-500">
                          Nenhuma movimentação encontrada com os filtros selecionados.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Paginação */}
              {totalPaginas > 1 && (
                <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-center items-center gap-2">
                  <button
                    onClick={() => setPaginaAtual(prev => Math.max(prev - 1, 1))}
                    disabled={paginaAtual === 1}
                    className={`px-3 py-1 rounded transition-colors ${paginaAtual === 1 ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                  >
                    Anterior
                  </button>
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(totalPaginas, 5) }, (_, i) => {
                      let num;
                      if (totalPaginas <= 5) num = i + 1;
                      else if (paginaAtual <= 3) num = i + 1;
                      else if (paginaAtual >= totalPaginas - 2) num = totalPaginas - 4 + i;
                      else num = paginaAtual - 2 + i;
                      return (
                        <button
                          key={num}
                          onClick={() => setPaginaAtual(num)}
                          className={`w-8 h-8 rounded transition-colors ${paginaAtual === num ? "bg-green-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
                        >
                          {num}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setPaginaAtual(prev => Math.min(prev + 1, totalPaginas))}
                    disabled={paginaAtual === totalPaginas}
                    className={`px-3 py-1 rounded transition-colors ${paginaAtual === totalPaginas ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                  >
                    Próxima
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}